package com.xtream.data_kmp.db.dao

import androidx.room3.EntityInsertAdapter
import androidx.room3.RoomDatabase
import androidx.room3.coroutines.createFlow
import androidx.room3.util.getColumnIndexOrThrow
import androidx.room3.util.getTotalChangedRows
import androidx.room3.util.performSuspending
import androidx.sqlite.SQLiteStatement
import com.xtream.data_kmp.db.tables.LiveStreamOrderEntity
import kotlin.Double
import kotlin.Int
import kotlin.Long
import kotlin.String
import kotlin.Suppress
import kotlin.Unit
import kotlin.collections.List
import kotlin.collections.MutableList
import kotlin.collections.mutableListOf
import kotlin.reflect.KClass
import kotlinx.coroutines.flow.Flow

@Suppress(names = ["UNCHECKED_CAST", "DEPRECATION", "REDUNDANT_PROJECTION", "REMOVAL", "MemberExtensionConflict"])
internal class LiveStreamOrderDao_Impl(
  __db: RoomDatabase,
) : LiveStreamOrderDao {
  private val __db: RoomDatabase

  private val __insertAdapterOfLiveStreamOrderEntity: EntityInsertAdapter<LiveStreamOrderEntity>
  init {
    this.__db = __db
    this.__insertAdapterOfLiveStreamOrderEntity = object : EntityInsertAdapter<LiveStreamOrderEntity>() {
      protected override fun createQuery(): String = "INSERT OR REPLACE INTO `live_stream_order` (`id`,`streamId`,`position`,`categoryId`,`updatedAt`) VALUES (nullif(?, 0),?,?,?,?)"

      protected override fun bind(statement: SQLiteStatement, entity: LiveStreamOrderEntity) {
        statement.bindLong(1, entity.id)
        statement.bindLong(2, entity.streamId.toLong())
        statement.bindDouble(3, entity.position)
        val _tmpCategoryId: Int? = entity.categoryId
        if (_tmpCategoryId == null) {
          statement.bindNull(4)
        } else {
          statement.bindLong(4, _tmpCategoryId.toLong())
        }
        statement.bindLong(5, entity.updatedAt)
      }
    }
  }

  public override suspend fun insert(order: LiveStreamOrderEntity): Unit = performSuspending(__db, false, true) { _connection ->
    __insertAdapterOfLiveStreamOrderEntity.insert(_connection, order)
  }

  public override suspend fun insertAll(orders: List<LiveStreamOrderEntity>): Unit = performSuspending(__db, false, true) { _connection ->
    __insertAdapterOfLiveStreamOrderEntity.insert(_connection, orders)
  }

  public override suspend fun getByStreamId(streamId: Int): LiveStreamOrderEntity? {
    val _sql: String = "SELECT * FROM live_stream_order WHERE streamId = ? LIMIT 1"
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindLong(_argIndex, streamId.toLong())
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfStreamId: Int = getColumnIndexOrThrow(_stmt, "streamId")
        val _columnIndexOfPosition: Int = getColumnIndexOrThrow(_stmt, "position")
        val _columnIndexOfCategoryId: Int = getColumnIndexOrThrow(_stmt, "categoryId")
        val _columnIndexOfUpdatedAt: Int = getColumnIndexOrThrow(_stmt, "updatedAt")
        val _result: LiveStreamOrderEntity?
        if (_stmt.step()) {
          val _tmpId: Long
          _tmpId = _stmt.getLong(_columnIndexOfId)
          val _tmpStreamId: Int
          _tmpStreamId = _stmt.getLong(_columnIndexOfStreamId).toInt()
          val _tmpPosition: Double
          _tmpPosition = _stmt.getDouble(_columnIndexOfPosition)
          val _tmpCategoryId: Int?
          if (_stmt.isNull(_columnIndexOfCategoryId)) {
            _tmpCategoryId = null
          } else {
            _tmpCategoryId = _stmt.getLong(_columnIndexOfCategoryId).toInt()
          }
          val _tmpUpdatedAt: Long
          _tmpUpdatedAt = _stmt.getLong(_columnIndexOfUpdatedAt)
          _result = LiveStreamOrderEntity(_tmpId,_tmpStreamId,_tmpPosition,_tmpCategoryId,_tmpUpdatedAt)
        } else {
          _result = null
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override fun observeAll(): Flow<List<LiveStreamOrderEntity>> {
    val _sql: String = "SELECT * FROM live_stream_order ORDER BY position ASC"
    return createFlow(__db, false, arrayOf("live_stream_order")) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfStreamId: Int = getColumnIndexOrThrow(_stmt, "streamId")
        val _columnIndexOfPosition: Int = getColumnIndexOrThrow(_stmt, "position")
        val _columnIndexOfCategoryId: Int = getColumnIndexOrThrow(_stmt, "categoryId")
        val _columnIndexOfUpdatedAt: Int = getColumnIndexOrThrow(_stmt, "updatedAt")
        val _result: MutableList<LiveStreamOrderEntity> = mutableListOf()
        while (_stmt.step()) {
          val _item: LiveStreamOrderEntity
          val _tmpId: Long
          _tmpId = _stmt.getLong(_columnIndexOfId)
          val _tmpStreamId: Int
          _tmpStreamId = _stmt.getLong(_columnIndexOfStreamId).toInt()
          val _tmpPosition: Double
          _tmpPosition = _stmt.getDouble(_columnIndexOfPosition)
          val _tmpCategoryId: Int?
          if (_stmt.isNull(_columnIndexOfCategoryId)) {
            _tmpCategoryId = null
          } else {
            _tmpCategoryId = _stmt.getLong(_columnIndexOfCategoryId).toInt()
          }
          val _tmpUpdatedAt: Long
          _tmpUpdatedAt = _stmt.getLong(_columnIndexOfUpdatedAt)
          _item = LiveStreamOrderEntity(_tmpId,_tmpStreamId,_tmpPosition,_tmpCategoryId,_tmpUpdatedAt)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun getByIndex(index: Int, categoryId: Int): LiveStreamOrderEntity? {
    val _sql: String = """
        |
        |        SELECT * FROM live_stream_order 
        |        WHERE categoryId = ? 
        |        ORDER BY position ASC 
        |        LIMIT 1 OFFSET ?
        |    
        """.trimMargin()
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindLong(_argIndex, categoryId.toLong())
        _argIndex = 2
        _stmt.bindLong(_argIndex, index.toLong())
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfStreamId: Int = getColumnIndexOrThrow(_stmt, "streamId")
        val _columnIndexOfPosition: Int = getColumnIndexOrThrow(_stmt, "position")
        val _columnIndexOfCategoryId: Int = getColumnIndexOrThrow(_stmt, "categoryId")
        val _columnIndexOfUpdatedAt: Int = getColumnIndexOrThrow(_stmt, "updatedAt")
        val _result: LiveStreamOrderEntity?
        if (_stmt.step()) {
          val _tmpId: Long
          _tmpId = _stmt.getLong(_columnIndexOfId)
          val _tmpStreamId: Int
          _tmpStreamId = _stmt.getLong(_columnIndexOfStreamId).toInt()
          val _tmpPosition: Double
          _tmpPosition = _stmt.getDouble(_columnIndexOfPosition)
          val _tmpCategoryId: Int?
          if (_stmt.isNull(_columnIndexOfCategoryId)) {
            _tmpCategoryId = null
          } else {
            _tmpCategoryId = _stmt.getLong(_columnIndexOfCategoryId).toInt()
          }
          val _tmpUpdatedAt: Long
          _tmpUpdatedAt = _stmt.getLong(_columnIndexOfUpdatedAt)
          _result = LiveStreamOrderEntity(_tmpId,_tmpStreamId,_tmpPosition,_tmpCategoryId,_tmpUpdatedAt)
        } else {
          _result = null
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun getAllSorted(categoryId: Int): List<LiveStreamOrderEntity> {
    val _sql: String = """
        |
        |        SELECT * FROM live_stream_order 
        |        WHERE categoryId = ? 
        |        ORDER BY position ASC
        |    
        """.trimMargin()
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindLong(_argIndex, categoryId.toLong())
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfStreamId: Int = getColumnIndexOrThrow(_stmt, "streamId")
        val _columnIndexOfPosition: Int = getColumnIndexOrThrow(_stmt, "position")
        val _columnIndexOfCategoryId: Int = getColumnIndexOrThrow(_stmt, "categoryId")
        val _columnIndexOfUpdatedAt: Int = getColumnIndexOrThrow(_stmt, "updatedAt")
        val _result: MutableList<LiveStreamOrderEntity> = mutableListOf()
        while (_stmt.step()) {
          val _item: LiveStreamOrderEntity
          val _tmpId: Long
          _tmpId = _stmt.getLong(_columnIndexOfId)
          val _tmpStreamId: Int
          _tmpStreamId = _stmt.getLong(_columnIndexOfStreamId).toInt()
          val _tmpPosition: Double
          _tmpPosition = _stmt.getDouble(_columnIndexOfPosition)
          val _tmpCategoryId: Int?
          if (_stmt.isNull(_columnIndexOfCategoryId)) {
            _tmpCategoryId = null
          } else {
            _tmpCategoryId = _stmt.getLong(_columnIndexOfCategoryId).toInt()
          }
          val _tmpUpdatedAt: Long
          _tmpUpdatedAt = _stmt.getLong(_columnIndexOfUpdatedAt)
          _item = LiveStreamOrderEntity(_tmpId,_tmpStreamId,_tmpPosition,_tmpCategoryId,_tmpUpdatedAt)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun delete(streamId: Int) {
    val _sql: String = "DELETE FROM live_stream_order WHERE streamId = ?"
    return performSuspending(__db, false, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindLong(_argIndex, streamId.toLong())
        _stmt.step()
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun deleteAll() {
    val _sql: String = "DELETE FROM live_stream_order"
    return performSuspending(__db, false, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        _stmt.step()
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun deleteAllFrom(categoryId: Int) {
    val _sql: String = "DELETE FROM live_stream_order WHERE categoryId = ?"
    return performSuspending(__db, false, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindLong(_argIndex, categoryId.toLong())
        _stmt.step()
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun shiftPositionsBetween(
    categoryId: Int?,
    lowerBound: Double,
    upperBound: Double,
    delta: Double,
  ): Int {
    val _sql: String = """
        |
        |    UPDATE live_stream_order
        |    SET position = ROUND(position + ?, 4)
        |    WHERE ((? IS NULL AND categoryId IS NULL) OR categoryId = ?)
        |      AND position > ?
        |      AND position < ?
        |""".trimMargin()
    return performSuspending(__db, false, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindDouble(_argIndex, delta)
        _argIndex = 2
        if (categoryId == null) {
          _stmt.bindNull(_argIndex)
        } else {
          _stmt.bindLong(_argIndex, categoryId.toLong())
        }
        _argIndex = 3
        if (categoryId == null) {
          _stmt.bindNull(_argIndex)
        } else {
          _stmt.bindLong(_argIndex, categoryId.toLong())
        }
        _argIndex = 4
        _stmt.bindDouble(_argIndex, lowerBound)
        _argIndex = 5
        _stmt.bindDouble(_argIndex, upperBound)
        _stmt.step()
        getTotalChangedRows(_connection)
      } finally {
        _stmt.close()
      }
    }
  }

  public companion object {
    public fun getRequiredColumnConverters(): List<KClass<*>> = emptyList()

    public fun getRequiredDaoReturnTypeConverters(): List<KClass<*>> = emptyList()
  }
}
